import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  MenuItem,
  TextField
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import useBind from '../../hooks/useBind';
import { selectCities } from '../../redux/slices/citiesSlice';
import {
  createAdminAsync,
  getAdminAsync,
  getAdminsAsync,
  selectAdmins,
  updateAdminAsync
} from '../../redux/slices/adminsSlice';
import { CreateAdminBody } from '../../resources/adminsTypes';
import { selectTabs } from '../../redux/slices/tabsSlice';
import { selectAuth } from '../../redux/slices/authSlice';
import {
  getManagementCompaniesAsync,
  selectManagementCompanies
} from '../../redux/slices/managementCompaniesSlice';
import { useGetAdminRolesQuery } from '../../redux/api/adminRolesApi';
import TabsSelectionDialog from './TabsSelectionDialog';

const initialState: CreateAdminBody = {
  login: '',
  cities: [],
  password: '',
  tabs: [],
  roleId: null,
  managementCompanies: []
};

const AdminsDialog = (
  props: DialogProps & { onClose: () => void; adminId?: string }
) => {
  const { open, onClose, adminId } = props;

  const dispatch = useDispatch();

  const citiesStore = useSelector(selectCities);
  const adminInfo = useSelector(selectAdmins);
  const auth = useSelector(selectAuth);
  const mcState = useSelector(selectManagementCompanies);

  const isSuperAdmin = useMemo(() => {
    return auth.info?.type === 'superAdmin';
  }, [auth.info?.type]);

  const cities = useMemo(
    () => (isSuperAdmin ? citiesStore.items : adminInfo.info?.cities || []),
    [isSuperAdmin, citiesStore.items, adminInfo.info]
  );
  const tabs = useSelector(selectTabs);

  const { data: roles = [], isLoading: isLoadingRoles } = useGetAdminRolesQuery();

  const [admin, setAdmin] = useState(initialState);
  const [tabsDialogOpen, setTabsDialogOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setTabsDialogOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      dispatch(getManagementCompaniesAsync());
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (adminId && open) {
      const getCity = async () => {
        const response: any = await dispatch(getAdminAsync(adminId));
        setAdmin({
          login: response.admin.login,
          cities: response.admin.cities?.map((city: any) => city._id) || [],
          password: response.admin.password,
          tabs: response.admin.tabs.map((tab: any) => tab._id),
          roleId: response.admin.role?._id || null,
          managementCompanies: (response.admin.managementCompanies || []).map(
            (c: any) => (typeof c === 'string' ? c : c._id)
          )
        });
      };

      getCity();
    } else {
      setAdmin(initialState);
    }
  }, [adminId, dispatch, open]);

  const bind = useBind(admin, setAdmin);

  const selectedTabsNames = useMemo(() => {
    return admin.tabs
      .map(tabId => {
        const tab = tabs.items.find(t => t._id === tabId);
        return tab ? tab.name : null;
      })
      .filter(Boolean)
      .join(', ');
  }, [admin.tabs, tabs.items]);

  useEffect(() => {
    if (admin.roleId && roles.length > 0) {
      const selectedRole = roles.find(role => role._id === admin.roleId);
      if (selectedRole && selectedRole.tabs) {
        setAdmin(prev => ({
          ...prev,
          tabs: selectedRole.tabs.map(tab => tab._id)
        }));
      }
    }
  }, [admin.roleId, roles]);

  const handleSave = useCallback(async () => {
    try {
      if (adminId) {
        await dispatch(updateAdminAsync(adminId, admin));
      } else {
        await dispatch(createAdminAsync(admin));
      }

      dispatch(getAdminsAsync());
      onClose();
    } catch (e: any) {
      if (e.response) {
        alert(e.response.data.message);
      }
    }
  }, [admin, adminId, dispatch, onClose]);

  return (
    <>
    <Dialog fullWidth maxWidth='sm' open={open} onClose={onClose}>
      <DialogTitle>
        {adminId ? 'Редагування адміністратора' : 'Створення адміністратора'}
      </DialogTitle>
      <DialogContent>
        {!adminId && (
          <TextField
            {...bind('login')}
            fullWidth
            autoComplete='new-password'
            label='Логін'
            margin='normal'
            variant='outlined'
          />
        )}
        <TextField
          {...bind('password')}
          fullWidth
          autoComplete='new-password'
          label='Пароль'
          margin='normal'
          type='password'
          variant='outlined'
        />
        <TextField
          {...bind('cities')}
          fullWidth
          select
          label='Міста'
          margin='normal'
          SelectProps={{
            multiple: true
          }}
          variant='outlined'
        >
          {cities.map(city => (
            <MenuItem key={city._id} value={city._id}>
              {city.cityName}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          {...bind('managementCompanies')}
          fullWidth
          select
          label='Управляючі компанії (готельний доступ)'
          margin='normal'
          SelectProps={{
            multiple: true
          }}
          variant='outlined'
          helperText="Дозвіл 'view_management_company' надається у вкладці «Таби»"
        >
          {mcState.companies.map(c => (
            <MenuItem key={c._id} value={c._id}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          label='Таби'
          margin='normal'
          variant='outlined'
          value={selectedTabsNames || 'Не вибрано'}
          onClick={() => setTabsDialogOpen(true)}
          InputProps={{
            readOnly: true,
            style: { cursor: 'pointer' }
          }}
          helperText={`Вибрано: ${admin.tabs.length} табів`}
        />
        <TextField
          {...bind('roleId')}
          fullWidth
          select
          label='Роль'
          margin='normal'
          variant='outlined'
          disabled={isLoadingRoles}
        >
          <MenuItem value="">
            <em>Без ролі</em>
          </MenuItem>
          {roles.map(role => (
            <MenuItem key={role._id} value={role._id}>
              {role.name}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button color='primary' onClick={handleSave}>
          Зберегти
        </Button>
      </DialogActions>
    </Dialog>
    
    <TabsSelectionDialog
      open={tabsDialogOpen}
      onClose={() => setTabsDialogOpen(false)}
      tabs={tabs.items}
      selectedTabs={admin.tabs}
      onSave={(selectedTabs) => {
        setAdmin({ ...admin, tabs: selectedTabs });
        setTabsDialogOpen(false);
      }}
    />
    </>
  );
};

export default AdminsDialog;
